import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';
import {
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  Star,
  MapPin,
  Loader2,
  ExternalLink
} from 'lucide-react';

const AdminProveedores = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    logo_url: '',
    website_url: '',
    location: '',
    discount_description: ''
  });
  const { toast } = useToast();

  const categories = [
    'Notaría', 'Avalúos', 'Financiamiento', 'Seguros', 
    'Marketing', 'Legal', 'Tecnología', 'Capacitación', 'Otro'
  ];

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await api.get('/admin/providers');
      setProviders(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los proveedores",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProvider) {
        await api.put(`/admin/providers/${editingProvider.id}`, formData);
        toast({ title: "Proveedor actualizado" });
      } else {
        await api.post('/admin/providers', formData);
        toast({ title: "Proveedor creado" });
      }
      
      fetchProviders();
      resetForm();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error al guardar",
        variant: "destructive"
      });
    }
  };

  const handleVerify = async (id) => {
    try {
      await api.post(`/admin/providers/${id}/verify`);
      toast({ title: "Proveedor verificado" });
      fetchProviders();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo verificar",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este proveedor?')) return;
    
    try {
      await api.delete(`/admin/providers/${id}`);
      toast({ title: "Proveedor eliminado" });
      fetchProviders();
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo eliminar",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setFormData({
      name: provider.name || '',
      category: provider.category || '',
      description: provider.description || '',
      logo_url: provider.logo_url || '',
      website_url: provider.website_url || '',
      location: provider.location || '',
      discount_description: provider.discount_description || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProvider(null);
    setFormData({
      name: '',
      category: '',
      description: '',
      logo_url: '',
      website_url: '',
      location: '',
      discount_description: ''
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Proveedores Certificados</h1>
            <p className="text-gray-600">Gestiona la red de proveedores UPIM</p>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-gray-900">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Proveedor
          </Button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <Card>
            <CardHeader>
              <CardTitle>{editingProvider ? 'Editar Proveedor' : 'Nuevo Proveedor'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Nombre</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Categoría</label>
                    <select
                      className="w-full rounded-md border border-gray-300 p-2"
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      required
                    >
                      <option value="">Seleccionar...</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700">Descripción</label>
                  <textarea
                    className="w-full rounded-md border border-gray-300 p-2"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Ubicación</label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="Ciudad de México"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Sitio Web</label>
                    <Input
                      value={formData.website_url}
                      onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                      placeholder="https://..."
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">URL del Logo</label>
                    <Input
                      value={formData.logo_url}
                      onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Descuento/Beneficio</label>
                    <Input
                      value={formData.discount_description}
                      onChange={(e) => setFormData({...formData, discount_description: e.target.value})}
                      placeholder="15% descuento en servicios"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-gray-900">
                    {editingProvider ? 'Actualizar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Providers List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : providers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No hay proveedores registrados</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {providers.map(provider => (
              <Card key={provider.id} className="overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    {/* Logo */}
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                      {provider.logo_url ? (
                        <img src={provider.logo_url} alt={provider.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Star className="h-6 w-6" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-bold text-gray-900 truncate">{provider.name}</h3>
                        {provider.is_verified && (
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        )}
                      </div>
                      <Badge variant="outline" className="mb-2">{provider.category}</Badge>
                      <p className="text-sm text-gray-600 line-clamp-2">{provider.description}</p>
                      
                      {provider.discount_description && (
                        <p className="text-sm text-green-600 font-medium mt-2">
                          {provider.discount_description}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-2 mt-3 text-xs text-gray-500">
                        {provider.location && (
                          <span className="flex items-center">
                            <MapPin className="h-3 w-3 mr-1" />
                            {provider.location}
                          </span>
                        )}
                        {provider.rating > 0 && (
                          <span className="flex items-center">
                            <Star className="h-3 w-3 mr-1 text-yellow-500" />
                            {provider.rating}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end space-x-2 mt-4 pt-4 border-t">
                    {!provider.is_verified && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-green-600"
                        onClick={() => handleVerify(provider.id)}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Verificar
                      </Button>
                    )}
                    {provider.website_url && (
                      <Button variant="outline" size="sm" asChild>
                        <a href={provider.website_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleEdit(provider)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600"
                      onClick={() => handleDelete(provider.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProveedores;
